import { useEffect, useRef, useState } from "react";
import { useBrand } from "@/lib/brand";
import { useNavigate, useParams } from "react-router-dom";
import WizardLayout from "@/components/WizardLayout";
import SignaturePad from "@/components/SignaturePad";
import PhotoUploader from "@/components/PhotoUploader";
import CatalogPicker from "@/components/CatalogPicker";
import PersonFields from "@/components/registry/PersonFields";
import { AvisoTablas } from "@/components/registry/AddressFields";
import { useRegistryRefdata } from "@/components/registry/useRegistryRefdata";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";
import { eur, fecha } from "@/lib/format";
import { pendienteAlquiler, pendienteExtras, restoTrasSenal } from "@/lib/reserva";
import {
  aplicarOcr, camposDeSiempre, cambiosOcr, limpiarPersona, nombrePartido, obligatorios, personaVacia, revisarPersona,
} from "@/lib/registro";
import { toast } from "sonner";
import { Wallet, ShieldCheck, Sparkles, Check as CheckIcon, PlayCircle, ExternalLink } from "lucide-react";


const STEPS = ["Datos", "Documentos", "Viaje", "Mejoras", "Firma", "Pago"];
const STEP_DATA = 0;
const STEP_DOCS = 1;
const STEP_TRIP = 2;
const STEP_UPGRADES = 3;
const STEP_SIGNATURE = 4;
const STEP_PAYMENT = 5;

// Lo que el OCR propone al titular, con su nombre para el cliente. El nombre leído no se propone: el del paso 1 ya
// está escrito (viene de la reserva) y el del carné son «apellidos y nombre» en una cadena.
const ETIQUETA_OCR = { dni_number: "Documento", birth_date: "Fecha de nacimiento", license_number: "Número del carné",
  license_issue_date: "Carné obtenido el", address: "Dirección" };
const juntar = (xs) => (xs.length > 1 ? `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}` : xs[0] || "");

// El paso donde se corrige un 422 del envío. Con la lista de pydantic, el más temprano de los campos rechazados (el
// cliente sigue desde ahí); lo que no se reconoce, en la firma, donde ya está. Con el texto del registro de viajeros
// (registry/checkin.py), el del titular o el del segundo conductor.
const PASO_DEL_CAMPO = {
  full_name: STEP_DATA, dni_number: STEP_DATA, license_number: STEP_DATA, license_issue_date: STEP_DATA,
  birth_date: STEP_DATA, address: STEP_DATA, phone: STEP_DATA, email: STEP_DATA, registry_person: STEP_DATA,
  dni_photo_front: STEP_DOCS, dni_photo_back: STEP_DOCS, license_photo: STEP_DOCS, countries_to_visit: STEP_TRIP,
  km_package: STEP_UPGRADES, additional_driver: STEP_UPGRADES, additional_driver_requested: STEP_UPGRADES,
};
function pasoDelError(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const pasos = detail.map((d) => PASO_DEL_CAMPO[Array.isArray(d?.loc) ? d.loc[1] : ""] ?? STEP_SIGNATURE);
    return Math.min(STEP_SIGNATURE, ...pasos);
  }
  const texto = String(detail || "");
  if (/Tus datos|Tu carné/.test(texto)) return STEP_DATA;
  if (/segundo conductor/i.test(texto)) return STEP_UPGRADES;
  return STEP_SIGNATURE;
}

// Lo que falta o hay que revisar de una persona, a la vista (y no solo un botón desactivado). Su `id` describe el
// botón «Siguiente» (`aria-describedby`): un lector de pantalla dice por qué no se puede seguir.
const AVISO_TITULAR = "ci-pendiente-titular";
const AVISO_CONDUCTOR = "ci-pendiente-conductor";
function Pendiente({ rev, canal, testid, id }) {
  const items = [...rev.faltan, ...rev.revisar];
  if (items.length === 0) return null;
  return (
    <p id={id} className="text-xs text-[#9C6512]" data-testid={testid}>
      {canal ? "La ley nos obliga a registrar estos datos (Real Decreto 933/2021). " : ""}
      {rev.revisar.length > 0 ? "Revisa" : "Falta"}: {items.join(", ")}.
    </p>
  );
}

function rentalDays(reservation) {
  if (!reservation) return 1;
  try {
    const d1 = new Date(reservation.pickup_date);
    const d2 = new Date(reservation.return_date);
    return Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
  } catch {
    return 1;
  }
}

export default function ClientPreCheckin() {
  const brand = useBrand();
  const { code } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [products, setProducts] = useState([]);
  const [selection, setSelection] = useState({}); // { product_id: qty }
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkinSubmitted, setCheckinSubmitted] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrSuggestion, setOcrSuggestion] = useState(null); // {fields, source}
  const [data, setData] = useState({
    full_name: "",
    dni_number: "",
    license_number: "",
    license_issue_date: "",
    birth_date: "",
    address: "",
    phone: "",
    email: "",
    countries_to_visit: "",
    dni_photo_front: "",
    dni_photo_back: "",
    license_photo: "",
    km_package: "100",
    additional_driver_requested: false,
    signature: "",
    accept_terms: false,
    whatsapp_opt_in: false,
  });
  // Segundo conductor (sin coste): datos + documentación en el pre-checkin
  const [driver, setDriver] = useState({
    full_name: "",
    dni_number: "",
    license_number: "",
    license_issue_date: "",
    birth_date: "",
    dni_photo_front: "",
    dni_photo_back: "",
    license_photo: "",
  });
  const setDrv = (k, v) => setDriver((d) => ({ ...d, [k]: v }));
  // Registro de viajeros (RD 933/2021): el titular y el segundo conductor se escriben en sus campos, y de ellos salen
  // los de siempre (`data` y `driver`: resumen del contrato, OCR y envío).
  const refdata = useRegistryRefdata();
  const [persona, setPersona] = useState(personaVacia());
  const [personaDrv, setPersonaDrv] = useState(personaVacia());
  // Un 422 del envío: se enseña en el paso donde se corrige y se quita en cuanto el cliente cambia algo.
  const [errorEnvio, setErrorEnvio] = useState(null);
  // El OCR del segundo conductor responde tarde: el aviso se calcula sobre lo último escrito (la referencia, que
  // también encadena dos lecturas seguidas) y el estado se cambia con una actualización funcional.
  const personaDrvRef = useRef(personaDrv);
  personaDrvRef.current = personaDrv;
  const driverRef = useRef(driver);
  driverRef.current = driver;
  useEffect(() => { setData((d) => ({ ...d, ...camposDeSiempre(persona, refdata) })); }, [persona, refdata]);
  // Del segundo conductor, lo que usan el contrato y la ficha; ni dirección, ni teléfono ni correo.
  useEffect(() => {
    const { full_name, dni_number, birth_date, license_number } = camposDeSiempre(personaDrv, refdata);
    setDriver((d) => ({ ...d, full_name, dni_number, birth_date, license_number }));
  }, [personaDrv, refdata]);
  useEffect(() => { setErrorEnvio(null); }, [persona, personaDrv, data, driver, selection]);

  const reload = async () => {
    const res = await api.get(`/r/${code}`);
    setReservation(res.data);
    return res.data;
  };

  useEffect(() => {
    Promise.all([api.get(`/r/${code}`), api.get(`/catalog`)])
      .then(([resR, resC]) => {
        const r = resR.data;
        setReservation(r);
        const catalogProducts = resC.data.products || [];
        setProducts(catalogProducts);
        setData((d) => ({
          ...d,
          km_package: r.km_package || "100",
          additional_driver_requested: Boolean(r.additional_driver_requested),
        }));
        setPersona((p) => ({ ...p, ...nombrePartido(r.client_name), phone: r.client_phone || "", email: r.client_email || "" }));
        // Rebuild the picker state from the stored snapshot, or preselect
        // catalog defaults (base insurance + included km)
        const initial = {};
        if ((r.selected_items || []).length > 0) {
          r.selected_items.forEach((it) => {
            initial[it.product_id] = it.qty;
          });
        } else {
          catalogProducts
            .filter((p) => p.meta?.is_default)
            .forEach((p) => {
              initial[p.id] = 1;
            });
        }
        setSelection(initial);
        if (r.has_checkin) {
          setCheckinSubmitted(true);
          setStep(STEP_PAYMENT);
        }
      })
      .catch((err) => {
        toast.error(formatError(err));
        navigate("/");
      });
  }, [code, navigate]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const days = rentalDays(reservation);
  const driverProduct = products.find((p) => p.meta?.is_additional_driver);
  const driverSelected = Boolean(driverProduct && selection[driverProduct.id] > 0);
  // Con canal se llevan y se exigen los datos del registro; sin él, el pre-checkin de siempre (modo básico).
  const canal = (reservation?.registry?.required && reservation.registry.channel) || null;
  const recogida = reservation?.pickup_date;
  const revTitular = revisarPersona(persona, { canal, recogida });
  const revDrv = revisarPersona(personaDrv, { canal, titular: false, recogida });
  const vale = (rev) => rev.faltan.length === 0 && rev.revisar.length === 0;
  let avisoSiguiente = null;
  if (step === STEP_DATA && !vale(revTitular)) avisoSiguiente = AVISO_TITULAR;
  else if (step === STEP_UPGRADES && driverSelected && !vale(revDrv)) avisoSiguiente = AVISO_CONDUCTOR;

  // OCR para el 2º conductor: autorrellena directamente los campos vacíos
  const runDriverOcr = async (rawImage, docType) => {
    if (reservation?.ocr_enabled === false) return;
    try {
      const { data: resp } = await api.post(`/r/${code}/ocr`, {
        image: rawImage,
        doc_type: docType,
      });
      if (resp.ok && resp.fields) {
        const opciones = { origen: docType };
        const antes = personaDrvRef.current;
        const despues = aplicarOcr(antes, resp.fields, opciones);
        const cambios = cambiosOcr(antes, despues);
        if (cambios.length > 0) {
          personaDrvRef.current = despues;
          setPersonaDrv((p) => aplicarOcr(p, resp.fields, opciones));
        }
        const expedido = resp.fields.license_issue_date;
        if (expedido && !driverRef.current.license_issue_date) {
          driverRef.current = { ...driverRef.current, license_issue_date: expedido };
          setDriver((d) => (d.license_issue_date ? d : { ...d, license_issue_date: expedido }));
          cambios.push("la fecha del carné");
        }
        if (cambios.length > 0) toast.success(`Datos del 2º conductor leídos del documento: ${juntar(cambios)}.`);
      }
    } catch {
      // best-effort
    }
  };

  // ---------- OCR autofill ----------
  const runOcr = async (rawImage, docType) => {
    // Sin OCR en la instalación no se envía la foto a leer ni se muestra «Leyendo documento...».
    if (reservation?.ocr_enabled === false) return;
    setOcrBusy(true);
    try {
      const { data: resp } = await api.post(`/r/${code}/ocr`, {
        image: rawImage,
        doc_type: docType,
      });
      if (resp.ok && resp.fields) {
        const nonEmpty = Object.fromEntries(
          Object.entries(resp.fields).filter(([k, v]) => v && k in ETIQUETA_OCR)
        );
        if (Object.keys(nonEmpty).length > 0) {
          setOcrSuggestion({ fields: nonEmpty, source: docType });
        }
      }
    } catch {
      // OCR is best-effort: the client can always type the data
    } finally {
      setOcrBusy(false);
    }
  };

  // «Aplicar a mis datos» es una acción explícita: cambia lo que el documento dice (documento, nacimiento, carné y su
  // fecha). La dirección no: es una línea y en el paso 1 va por campos.
  const applyOcr = () => {
    if (!ocrSuggestion) return;
    const { fields, source } = ocrSuggestion;
    const despues = aplicarOcr(persona, fields, { sobrescribir: true, origen: source });
    const cambios = cambiosOcr(persona, despues);
    setPersona(despues);
    if (fields.license_issue_date && fields.license_issue_date !== data.license_issue_date) {
      set("license_issue_date", fields.license_issue_date);
      cambios.push("la fecha del carné");
    }
    setOcrSuggestion(null);
    const direccion = fields.address ? " La dirección no se aplica: escríbela en el paso 1." : "";
    if (cambios.length > 0) toast.success(`Aplicado a tus datos: ${juntar(cambios)}. Revísalos en el paso 1.${direccion}`);
    else toast.info(`Lo leído en el documento ya coincidía con tus datos.${direccion}`);
  };

  // ---------- Steps ----------
  const isStepValid = () => {
    switch (step) {
      case STEP_DATA:
        return vale(revTitular);
      case STEP_DOCS:
        return data.dni_photo_front && data.dni_photo_back && data.license_photo;
      case STEP_TRIP:
        return true;
      case STEP_UPGRADES:
        // Con 2º conductor seleccionado, exigimos sus datos y documentación
        if (driverSelected) {
          return Boolean(vale(revDrv) && driver.dni_photo_front && driver.dni_photo_back && driver.license_photo);
        }
        return true;
      case STEP_SIGNATURE:
        return data.signature && data.accept_terms;
      case STEP_PAYMENT:
        return true;
      default:
        return false;
    }
  };

  const saveSelection = async () => {
    const items = Object.entries(selection)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, qty]) => ({ product_id, qty }));
    const { data: resp } = await api.post(`/r/${code}/items`, { items });
    await reload();
    return resp;
  };

  const submitCheckin = async () => {
    setLoading(true);
    setErrorEnvio(null);
    try {
      const resp = await api.post(`/r/${code}/checkin`, {
        ...data,
        registry_person: limpiarPersona(persona, { basico: !canal }),
        additional_driver_requested: driverSelected,
        additional_driver: driverSelected
          ? { ...driver, registry_person: limpiarPersona(personaDrv, { basico: !canal, titular: false }) } : null,
      });
      await reload();
      setCheckinSubmitted(true);
      toast.success("Pre-Checkin registrado");
      setStep(STEP_PAYMENT);
      return resp.data;
    } catch (err) {
      // Lo que el servidor rechaza (422) se corrige en su paso, con el mensaje a la vista; lo demás, aviso.
      if (err?.response?.status === 422) {
        const paso = pasoDelError(err);
        setErrorEnvio({ texto: formatError(err).replace(/[.\s]+$/, ""), paso });
        setStep(paso);
      } else {
        toast.error(formatError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const startPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/checkout/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const startSignalPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/signal/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const startExtrasPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/extras/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const startRentalPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/rental/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const onNext = async () => {
    if (step === STEP_UPGRADES) {
      setLoading(true);
      try {
        await saveSelection();
        setStep(STEP_SIGNATURE);
      } catch (err) {
        toast.error(formatError(err));
      } finally {
        setLoading(false);
      }
      return;
    }
    if (step === STEP_SIGNATURE) {
      await submitCheckin();
      return;
    }
    if (step === STEP_PAYMENT) {
      // Recogida lejana con señal sin pagar: primero la señal, y nada más.
      // El resto y la fianza se piden por correo antes de la entrega.
      if (reservation?.payment_plan?.stage === "signal") {
        await startSignalPayment();
        return;
      }
      const depositOk =
        reservation?.payment_status === "paid" || reservation?.payment_status === "on_hold";
      if (!depositOk) {
        await startPayment();
        return;
      }
      // Lo que queda según el plan: lo que ya pagó la señal no se vuelve a pedir (ni un pago de 0 €).
      if (pendienteAlquiler(reservation) > 0) {
        await startRentalPayment();
        return;
      }
      if (pendienteExtras(reservation) > 0) {
        await startExtrasPayment();
        return;
      }
      navigate(`/reserva/${code}`);
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const onBack = () => {
    if (step === 0) {
      navigate(`/reserva/${code}`);
      return;
    }
    if (checkinSubmitted) return;
    setStep((s) => Math.max(0, s - 1));
  };

  if (!reservation) return null;

  const depositLocked =
    reservation.payment_status === "paid" || reservation.payment_status === "on_hold";
  const extrasAmount = Number(reservation.extras_amount || 0);
  const extrasPaid = reservation.extras_payment_status === "paid";
  const rentalAmount = Number(reservation.total_amount || 0);
  // Plan de cobro del backend (payment_plan.py): qué toca pagar ahora.
  const plan = reservation.payment_plan || {};
  const signalStage = plan.stage === "signal";
  const signalPaid = reservation.signal_payment_status === "paid";
  // Lo que queda del alquiler y de los extras tras la señal: lo que ella ya pagó no se pide otra vez.
  const rentalDue = pendienteAlquiler(reservation);
  const rentalPending = rentalDue > 0;
  const extrasDue = pendienteExtras(reservation);
  const extrasPending = extrasDue > 0;
  const rentalPaid = rentalAmount > 0 && reservation.rental_payment_status === "paid";
  const selectedSnapshot = reservation.selected_items || [];

  const stepTitles = {
    [STEP_DATA]: "Tus datos",
    [STEP_DOCS]: "Documentación",
    [STEP_TRIP]: "Tu viaje",
    [STEP_UPGRADES]: "Mejora tu viaje",
    [STEP_SIGNATURE]: "Firma del contrato",
    [STEP_PAYMENT]: "Pago",
  };

  const nextLabel = (() => {
    if (step === STEP_UPGRADES) return "Guardar y continuar";
    if (step === STEP_SIGNATURE) return "Confirmar y firmar";
    if (step === STEP_PAYMENT) {
      if (signalStage) return `Pagar señal ${eur(plan.signal_amount)}`;
      if (!depositLocked) {
        return rentalPending
          ? (signalPaid ? "Pagar resto y fianza" : "Pagar alquiler y fianza")
          : extrasPending ? "Pagar fianza y extras" : `Pagar fianza ${eur(reservation.deposit_amount)}`;
      }
      if (rentalPending) return `Pagar ${signalPaid ? "resto" : "alquiler"} ${eur(rentalDue)}`;
      if (extrasPending) return `Pagar extras ${eur(extrasDue)}`;
      return "Volver a mi reserva";
    }
    return "Siguiente";
  })();

  return (
    <WizardLayout
      title={stepTitles[step] || ""}
      step={step}
      totalSteps={STEPS.length}
      stepLabel={`Pre-Checkin · ${STEPS[step]}`}
      onBack={onBack}
      onNext={onNext}
      nextLabel={nextLabel}
      nextDisabled={!isStepValid()}
      nextDescribedBy={avisoSiguiente}
      isLastStep={false}
      loading={loading}
    >
      {errorEnvio && step === errorEnvio.paso && (
        <div role="alert" className="max-w-xl mb-4 rounded-lg border border-acento/40 bg-acento/10 px-3 py-2 text-sm text-[#8C3B26]"
          data-testid="ci-submit-error">
          No se ha podido enviar el pre-checkin: {errorEnvio.texto}. Corrígelo y sigue hasta el final para enviarlo
          (tu firma se conserva).
        </div>
      )}
      {/* Un solo aviso si no llegan las tablas del registro, en el paso que tenga los datos de una persona. */}
      {(step === STEP_DATA || (step === STEP_UPGRADES && driverSelected)) && refdata?.failed && (
        <div className="max-w-xl mb-4"><AvisoTablas refdata={refdata} /></div>
      )}
      {step === STEP_DATA && (
        <div className="space-y-4 max-w-xl">
          <p className="text-[#5C5C5C]">
            Necesitamos tus datos personales para preparar el contrato de
            alquiler antes de la entrega del vehículo.
          </p>
          {/* Información básica de protección de datos (RGPD, art. 13) antes de enviar nada. */}
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-4 text-xs text-[#5C5C5C] leading-relaxed space-y-1" data-testid="ci-privacy-info">
            <div className="font-semibold text-[#1A1A1A] text-sm">Protección de datos</div>
            <p>
              <strong>Responsable:</strong> {brand?.legal_name || brand?.brand_name || "la empresa de alquiler"}
              {brand?.tax_id ? ` (NIF ${brand.tax_id})` : ""}.
            </p>
            <p>
              <strong>Para qué:</strong> identificarte y comprobar que puedes conducir (DNI o NIE, carné y sus
              fotos), preparar y firmar el contrato, y gestionar los pagos y la fianza
              {reservation.self_service ? "; en la recogida y la devolución autónomas, comprobar con la ubicación de tu móvil que estás junto al vehículo cuando pides abrirlo o cerrarlo" : ""}
              {canal ? "; y llevar el registro de viajeros que exige la ley" : ""}.
            </p>
            <p>
              <strong>Base jurídica:</strong> el contrato de alquiler y las obligaciones legales, como
              identificar al conductor ante una multa.
              {canal ? " También la obligación legal de llevar el registro de viajeros (Real Decreto 933/2021)." : ""}
            </p>
            <p>
              <strong>Destinatarios:</strong> Avant Servicios IT, SLU, que presta la plataforma como encargado
              del tratamiento;
              {reservation?.ocr_enabled === true && " Mistral AI (Francia), que lee las fotos de tus documentos para rellenar tus datos, por encargo de Avant;"}
              {" "}Stripe, para los pagos;
              {canal === "mossos" && " los Mossos d'Esquadra (Generalitat de Catalunya), a los que la ley obliga a comunicar los datos del contrato, de los conductores y del vehículo (Real Decreto 933/2021);"}
              {canal === "ses" && " el Ministerio del Interior (SES.HOSPEDAJES), al que la ley obliga a comunicar los datos del contrato, de los conductores y del vehículo (Real Decreto 933/2021);"}
              {" "}y la aseguradora y las autoridades cuando haga falta.
            </p>
            <p>
              <strong>Derechos:</strong> acceso, rectificación, supresión, oposición, limitación y portabilidad,
              escribiendo a {brand?.email || "la empresa"}. Puedes reclamar ante la Agencia Española de
              Protección de Datos.
            </p>
            <p>
              <a href="/privacidad?lang=es" target="_blank" rel="noopener noreferrer" className="underline text-marca" data-testid="ci-privacy-link">
                Política de privacidad completa
              </a>
            </p>
          </div>
          <PersonFields value={persona} onChange={setPersona} refdata={refdata} canal={canal} testid="ci-person"
            avisarTablas={false} basico={!canal} obligatorios={obligatorios(persona, { canal })} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ci-license-issue">Carné obtenido el</Label>
              <Input id="ci-license-issue" type="date" value={data.license_issue_date} onChange={(e) => set("license_issue_date", e.target.value)} className="mt-1 bg-white border-[#DCD8D0]" data-testid="ci-license-issue" />
            </div>
          </div>
          <Pendiente rev={revTitular} canal={canal} testid="ci-person-missing" id={AVISO_TITULAR} />
        </div>
      )}

      {step === STEP_DOCS && (
        <div className="space-y-5 max-w-xl">
          {/* La lectura automática solo se promete si la instalación la tiene (ocr_enabled). */}
          <p className="text-[#5C5C5C]">
            Sube fotos claras de tu DNI (ambas caras) y tu carné de conducir.
            {reservation.ocr_enabled === true && " Leeremos los datos automáticamente para ahorrarte teclear."}
          </p>
          {ocrBusy && (
            <div className="bg-marca/5 border border-marca/20 rounded-xl p-3 text-sm text-marca flex items-center gap-2" data-testid="ci-ocr-busy">
              <Sparkles className="h-4 w-4 animate-pulse" /> Leyendo documento...
            </div>
          )}
          {ocrSuggestion && (
            <div className="bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-xl p-4" data-testid="ci-ocr-suggestion">
              <div className="text-sm font-semibold text-[#9C6512] mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Datos detectados en el documento
              </div>
              <ul className="text-sm text-[#5C5C5C] space-y-0.5 mb-3">
                {Object.entries(ocrSuggestion.fields).map(([k, v]) => (
                  <li key={k}><span className="text-[#8A8A8A]">{ETIQUETA_OCR[k]}:</span> <strong className="text-[#1A1A1A]">{v}</strong></li>
                ))}
              </ul>
              {ocrSuggestion.fields.address && (
                <p className="text-xs text-[#9C6512] mb-3">
                  La dirección no se aplica sola: escríbela en el paso 1, con su código postal y su municipio.
                </p>
              )}
              <div className="flex gap-2">
                {Object.keys(ocrSuggestion.fields).some((k) => k !== "address") && (
                  <Button size="sm" onClick={applyOcr} className="bg-marca hover:bg-marca-hover text-white" data-testid="ci-ocr-apply">
                    Aplicar a mis datos
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setOcrSuggestion(null)} className="border-[#DCD8D0]">
                  Descartar
                </Button>
              </div>
            </div>
          )}
          <PhotoUploader
            label="DNI / NIE — Anverso"
            value={data.dni_photo_front}
            onChange={(v) => set("dni_photo_front", v)}
            uploadCode={code}
            onRaw={(raw) => runOcr(raw, "dni_front")}
            testid="ci-photo-dni-front"
          />
          <PhotoUploader
            label="DNI / NIE — Reverso"
            value={data.dni_photo_back}
            onChange={(v) => set("dni_photo_back", v)}
            uploadCode={code}
            onRaw={(raw) => runOcr(raw, "dni_back")}
            testid="ci-photo-dni-back"
          />
          <PhotoUploader
            label="Carné de conducir"
            value={data.license_photo}
            onChange={(v) => set("license_photo", v)}
            uploadCode={code}
            onRaw={(raw) => runOcr(raw, "license")}
            testid="ci-photo-license"
          />
        </div>
      )}

      {step === STEP_TRIP && (
        <div className="space-y-4 max-w-xl">
          <p className="text-[#5C5C5C]">
            Indícanos por dónde tienes previsto viajar. España continental
            está incluida por defecto.
          </p>
          <div>
            <Label>Países a visitar</Label>
            <Textarea value={data.countries_to_visit} onChange={(e) => set("countries_to_visit", e.target.value)} className="mt-2 bg-white border-[#DCD8D0]" rows={3} placeholder="Ej. España, Francia, Portugal" data-testid="ci-countries" />
          </div>
        </div>
      )}

      {step === STEP_UPGRADES && (
        <div className="max-w-xl" data-testid="ci-upgrades-step">
          <p className="text-[#5C5C5C] mb-5">
            Personaliza tu viaje: cobertura, kilómetros y extras. Todo se
            cobra junto a la fianza en un único pago seguro con Stripe.
          </p>
          <CatalogPicker
            products={products}
            days={days}
            value={selection}
            onChange={setSelection}
          />

          {driverSelected && (
            <div className="mt-6 bg-white border-2 border-marca rounded-xl p-5 space-y-4" data-testid="ci-driver-form">
              <div>
                <h4 className="font-display font-bold text-[#1A1A1A]">Segundo conductor</h4>
                <p className="text-sm text-[#5C5C5C] mt-0.5">
                  Sin coste. Necesitamos sus datos y documentación para
                  incluirlo en el contrato y el seguro.
                </p>
              </div>
              {/* Sin autocompletar: el navegador propondría los datos del titular, que es quien escribe. */}
              <PersonFields value={personaDrv} onChange={setPersonaDrv} refdata={refdata} canal={canal} testid="ci-drv-person"
                autoCompletar={false} avisarTablas={false} basico={!canal} titular={false}
                obligatorios={obligatorios(personaDrv, { canal, titular: false })} />
              {/* La fecha del carné no es del registro, pero el contrato la lleva (como la del titular). */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ci-drv-issue">Carné obtenido el</Label>
                  <Input id="ci-drv-issue" type="date" autoComplete="off" value={driver.license_issue_date} onChange={(e) => setDrv("license_issue_date", e.target.value)} className="mt-1 bg-white border-[#DCD8D0]" data-testid="ci-drv-issue" />
                </div>
              </div>
              <Pendiente rev={revDrv} canal={canal} testid="ci-drv-person-missing" id={AVISO_CONDUCTOR} />
              <PhotoUploader
                label="DNI / NIE del 2º conductor — Anverso"
                value={driver.dni_photo_front}
                onChange={(v) => setDrv("dni_photo_front", v)}
                uploadCode={code}
                onRaw={(raw) => runDriverOcr(raw, "dni_front")}
                testid="ci-drv-dni-front"
              />
              <PhotoUploader
                label="DNI / NIE del 2º conductor — Reverso"
                value={driver.dni_photo_back}
                onChange={(v) => setDrv("dni_photo_back", v)}
                uploadCode={code}
                onRaw={(raw) => runDriverOcr(raw, "dni_back")}
                testid="ci-drv-dni-back"
              />
              <PhotoUploader
                label="Carné de conducir del 2º conductor"
                value={driver.license_photo}
                onChange={(v) => setDrv("license_photo", v)}
                uploadCode={code}
                onRaw={(raw) => runDriverOcr(raw, "license")}
                testid="ci-drv-license"
              />
            </div>
          )}
        </div>
      )}

      {step === STEP_SIGNATURE && (
        <div className="space-y-5 max-w-xl">
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-5 text-sm text-[#5C5C5C] leading-relaxed max-h-72 overflow-y-auto">
            <h4 className="font-display font-bold text-[#1A1A1A] mb-2">Resumen del contrato</h4>
            <p className="mb-2">
              <strong>{data.full_name}</strong> (DNI {data.dni_number}) reconoce
              haber recibido la información del estado del vehículo{" "}
              {reservation?.vehicle?.brand_model || reservation?.vehicle?.name || ""}
              {reservation?.vehicle?.plate ? ` (matrícula ${reservation.vehicle.plate})` : ""} y lo
              inspeccionará presencialmente con el propietario el día de la entrega.
            </p>
            <p className="mb-2">
              Periodo de alquiler: <strong>{fecha(reservation.pickup_date)} {reservation.pickup_time}</strong>
              {" → "}<strong>{fecha(reservation.return_date)} {reservation.return_time}</strong>.
              Lugar de devolución: {reservation.return_location}.
            </p>
            {selectedSnapshot.length > 0 && (
              <div className="mb-2">
                Mejoras y extras contratados:
                <ul className="list-disc pl-5 mt-1">
                  {selectedSnapshot.map((it) => (
                    <li key={it.product_id}>
                      {it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}
                      {it.subtotal > 0 ? ` — ${eur(it.subtotal)}` : " — incluido"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {driverSelected && driver.full_name && (
              <p className="mb-2">
                Segundo conductor autorizado (sin coste): <strong>{driver.full_name}</strong> (DNI {driver.dni_number}).
              </p>
            )}
            <p className="mb-2">
              Importe total alquiler: <strong>{eur(reservation.total_amount)}</strong>.
              {extrasAmount > 0 && <> Extras: <strong>{eur(extrasAmount)}</strong>.</>}
              {" "}Fianza: <strong>{reservation.deposit_amount} €</strong>.
            </p>
            <p>
              El viajero se compromete a devolver el vehículo en su estado
              original (limpieza, depósitos vaciados/llenados, combustible
              equivalente al de salida) y reconoce las coberturas del seguro y
              franquicias. Autoriza el uso de la tarjeta de la fianza para
              cargos posteriores justificados (exceso de km, daños o extras
              pendientes) conforme al contrato.
            </p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={data.accept_terms} onCheckedChange={(v) => set("accept_terms", Boolean(v))} data-testid="ci-accept-terms" />
            <span className="text-sm text-[#1A1A1A]">
              He leído y acepto las condiciones del contrato de alquiler.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={data.whatsapp_opt_in} onCheckedChange={(v) => set("whatsapp_opt_in", Boolean(v))} data-testid="ci-whatsapp-optin" />
            <span className="text-sm text-[#1A1A1A]">
              Quiero recibir los avisos de mi reserva también por WhatsApp (opcional).
            </span>
          </label>
          <div>
            <Label>Firma</Label>
            <div className="mt-2">
              <SignaturePad value={data.signature} onChange={(v) => set("signature", v)} testid="ci-signature" />
            </div>
          </div>
        </div>
      )}

      {step === STEP_PAYMENT && (
        <div className="max-w-xl space-y-5" data-testid="ci-payment-step">
          {signalStage ? (
            <div className="bg-white border border-[#DCD8D0] rounded-xl p-6" data-testid="ci-signal-card">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-marca text-white flex items-center justify-center shrink-0">
                  <Wallet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-xl font-bold text-[#1A1A1A]">Señal para confirmar la reserva</h3>
                  <div className="mt-3 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      {/* La señal se calcula sobre alquiler más extras, y el resto incluye los extras sin cobrar. */}
                      <span className="text-[#5C5C5C]">Señal ({Number(plan.signal_percent).toFixed(0)} % del total)</span>
                      <strong>{eur(plan.signal_amount)}</strong>
                    </div>
                    <div className="flex justify-between text-[#8A8A8A]">
                      <span>Resto · antes de la entrega</span>
                      <span data-testid="ci-signal-rest">{eur(restoTrasSenal(reservation))}</span>
                    </div>
                    <div className="flex justify-between text-[#8A8A8A]">
                      <span>Fianza · antes de la entrega</span>
                      <span>{Number(reservation.deposit_amount).toFixed(0)} €</span>
                    </div>
                  </div>
                  <p className="text-xs text-[#8A8A8A] mt-3">
                    Pago seguro con Stripe. Ahora solo se cobra la señal. Unos días
                    antes de la recogida te enviaremos un correo para pagar el resto
                    y la fianza. La fianza se retiene en tu tarjeta sin cobrarla, salvo
                    que al pagarla falten más de {plan.hold_max_days ?? 6} días para la
                    devolución: entonces se cobra y se te devuelve tras la devolución
                    sin incidencias.
                  </p>
                </div>
              </div>
            </div>
          ) : depositLocked ? (
            <div className="bg-[#4A7C59]/10 border border-[#4A7C59]/30 rounded-xl p-6 text-center">
              <ShieldCheck className="h-10 w-10 text-[#4A7C59] mx-auto mb-3" />
              <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-1">
                ¡Fianza confirmada!
              </h3>
              <p className="text-sm text-[#5C5C5C]">
                {reservation.payment_status === "on_hold"
                  ? `Tu fianza de ${reservation.deposit_amount} € está retenida en tu tarjeta (preautorización, sin cobro) y se liberará tras la devolución sin incidencias.`
                  : `Tu fianza de ${reservation.deposit_amount} € se ha cobrado y se te devolverá tras la devolución sin incidencias.`}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-acento text-white flex items-center justify-center shrink-0">
                  <Wallet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-xl font-bold text-[#1A1A1A]">Un único pago</h3>
                  <div className="mt-3 space-y-1.5 text-sm">
                    {rentalPending && (
                      <div className="flex justify-between" data-testid="ci-pay-rental-line">
                        <span className="text-[#5C5C5C]">{signalPaid ? "Resto del alquiler" : "Alquiler"}</span>
                        <strong>{eur(rentalDue)}</strong>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[#5C5C5C]">Fianza (reembolsable)</span>
                      <strong>{eur(reservation.deposit_amount)}</strong>
                    </div>
                    {extrasPending && (
                      <div className="flex justify-between">
                        <span className="text-[#5C5C5C]">Mejoras y extras</span>
                        <strong>{eur(extrasDue)}</strong>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-[#8A8A8A] mt-3">
                    Pago seguro con Stripe.{" "}
                    {plan.deposit_mode === "charge"
                      ? `Como faltan más de ${plan.hold_max_days ?? 6} días para la devolución, la fianza se cobra y se te devuelve tras la devolución sin incidencias`
                      : "La fianza se retiene en tu tarjeta sin cobrarla y se libera tras la devolución sin incidencias"};{" "}
                    {rentalPending ? "el alquiler y los extras se cobran" : "los extras se cobran"} con
                    la misma tarjeta.
                  </p>
                </div>
              </div>
            </div>
          )}

          {checkinSubmitted && brand?.guide_url && (
            <a
              href={brand.guide_url}
              target="_blank"
              rel="noreferrer"
              className="block bg-marca hover:bg-marca-hover transition-colors rounded-xl p-5 text-white"
              data-testid="ci-videos-card"
            >
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                  <PlayCircle className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-lg">
                    Aprende a usar tu camper antes de la recogida
                  </div>
                  <div className="text-sm text-white/80 mt-0.5">
                    Vídeos cortos sobre el equipamiento: cocina, aguas,
                    calefacción... Todo en la guía de {brand.brand_name}
                  </div>
                </div>
                <ExternalLink className="h-5 w-5 shrink-0 text-white/70" />
              </div>
            </a>
          )}

          {depositLocked && rentalPending && (
            <div className="bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-xl p-4" data-testid="ci-rental-pending">
              <div className="text-sm font-semibold text-[#9C6512] mb-1">
                Alquiler pendiente: {eur(rentalDue)}
              </div>
              <p className="text-xs text-[#9C6512]">
                Pulsa el botón para completar el pago del alquiler de forma
                segura con Stripe.
              </p>
            </div>
          )}
          {depositLocked && rentalPaid && (
            <div className="bg-[#4A7C59]/10 border border-[#4A7C59]/30 rounded-xl p-4 flex items-center gap-3" data-testid="ci-rental-ok">
              <CheckIcon className="h-5 w-5 text-[#4A7C59]" />
              <div className="text-sm text-[#1A1A1A]">Alquiler pagado: {eur(rentalAmount)}</div>
            </div>
          )}
          {depositLocked && (
            extrasPaid && extrasAmount > 0 ? (
              <div className="bg-[#4A7C59]/10 border border-[#4A7C59]/30 rounded-xl p-4 flex items-center gap-3" data-testid="ci-extras-ok">
                <CheckIcon className="h-5 w-5 text-[#4A7C59]" />
                <div className="text-sm text-[#1A1A1A]">Extras pagados: {eur(extrasAmount)}. ¡Todo listo para la entrega!</div>
              </div>
            ) : extrasPending ? (
              <div className="bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-xl p-4" data-testid="ci-extras-pending">
                <div className="text-sm font-semibold text-[#9C6512] mb-1">Extras pendientes: {eur(extrasDue)}</div>
                <p className="text-xs text-[#9C6512]">
                  No se pudieron cobrar automáticamente. Pulsa el botón para
                  completar el pago con Stripe.
                </p>
              </div>
            ) : null
          )}
        </div>
      )}
    </WizardLayout>
  );
}
